import type { Logger } from "@hiddentao/logger"
import { SyncItemType, SyncMessageType } from "../sync"
import { PendingAsyncActions } from "../utils/async"
import { getTagColor } from "../utils/colors"
import { generateId } from "../utils/ids"
import { ClientDataStorageEngine } from "./clientDataStorageEngine"
import { type GlobalTagsList, type Tag } from "./contentTypes"
import { type Item, type ItemUpdateData, type Revision } from "./types"

// Define a callback type for sending updates to a server
export type SendUpdateCallback = (
  messageType: SyncMessageType,
  data: any,
) => void

// Configuration interface for ContentProcessor constructor
export interface ContentProcessorConfig {
  dataStorage: ClientDataStorageEngine
  pendingActions: PendingAsyncActions
  isAuthenticated?: boolean
  encryptionKey?: Uint8Array | null
  connectionStatus?: string
  sendUpdate?: SendUpdateCallback
  logger: Logger
}

/**
 * Service responsible for processing content updates with a chain of responsibility pattern
 * This centralizes the content update logic and can be used in any context
 */
export class ContentProcessor {
  private dataStorage: ClientDataStorageEngine
  private pendingActions: PendingAsyncActions
  private isAuthenticated: boolean
  private encryptionKey: Uint8Array | null
  private connectionStatus: string
  private sendUpdate: SendUpdateCallback
  private logger: Logger

  constructor(config: ContentProcessorConfig) {
    this.dataStorage = config.dataStorage
    this.pendingActions = config.pendingActions
    this.isAuthenticated = config.isAuthenticated ?? false
    this.encryptionKey = config.encryptionKey ?? null
    this.connectionStatus = config.connectionStatus ?? ""
    this.sendUpdate =
      config.sendUpdate ??
      (() => {
        /* no-op */
      })
    this.logger = config.logger.child("ContentProcessor")
  }

  /**
   * Update service dependencies - used when auth state changes
   */
  public updateDependencies(
    isAuthenticated: boolean,
    encryptionKey: Uint8Array | null,
    connectionStatus: string,
    sendUpdate: SendUpdateCallback,
    logger: Logger = this.logger,
  ): void {
    this.isAuthenticated = isAuthenticated
    this.encryptionKey = encryptionKey
    this.connectionStatus = connectionStatus
    this.sendUpdate = sendUpdate
    this.logger = logger
    this.logger.debug("ContentProcessor dependencies updated")
  }

  /**
   * Process multiple item updates in batch - saves to storage and optionally sends to server
   * @param itemUpdates Array of item updates to process
   * @param sendToServer Whether to send updates to server
   * @param syncMessageType Message type for sync
   * @returns Array of updated items and revisions
   */
  async processItemUpdates(
    itemUpdates: ItemUpdateData[],
    sendToServer: boolean = true,
    syncMessageType: SyncMessageType = SyncMessageType.UPDATE_ITEMS,
  ): Promise<Array<{ item: Item; revision: Revision }>> {
    if (!this.isAuthenticated) {
      throw new Error("User is not authenticated")
    }

    // Ensure the encryption key is set
    if (!this.encryptionKey) {
      this.logger.error("Cannot update items - encryption key not available")
      throw new Error("Encryption key is not available")
    }

    // Track all updates that need to be processed
    const allUpdates: Array<{ item: Item; revision: Revision }> = []

    this.logger.info("Processing batch item updates", {
      count: itemUpdates.length,
      sendToServer,
    })

    // Get the global tags item ID
    const globalTagsId = this.dataStorage.getGlobalTagsItemId()

    // Validate that if we're updating global tags, it must be the only update in the batch
    if (itemUpdates.length > 0 && itemUpdates[0].itemId === globalTagsId) {
      if (itemUpdates.length !== 1) {
        throw new Error("Invalid batch update with global tags")
      } else {
        this.logger.info("Processing global tags list update")

        // Extract tags from content and call updateGlobalTags
        const tags = itemUpdates[0].content?.tags || []
        const tagUpdates = await this.updateGlobalTags(tags, false) // Don't send to server yet

        // Add all tag-related updates to our batch
        allUpdates.push(...tagUpdates)
      }
    } else {
      // Process each item update
      for (const updateData of itemUpdates) {
        const { itemId, type, content } = updateData

        // Check if this is a global tags list update
        if (itemId === globalTagsId) {
          throw new Error("Invalid batch update with global tags")
        }

        // Process tags if needed for this item
        if (type === SyncItemType.Note || type === SyncItemType.Task) {
          await this.processTagsUpdate(content, allUpdates)
        }

        // Update the main item
        const { item, revision } = await this.dataStorage.updateItemPlaintext(
          itemId,
          type as SyncItemType,
          content,
        )

        // Add the main item update to the list of updates
        allUpdates.push({ item, revision })
      }
    }

    // Send to server if required
    if (sendToServer) {
      await this.sendUpdatesToServer(allUpdates, syncMessageType)
    }

    return allUpdates
  }

  /**
   * Process tags from content and update global tag list if needed
   * @param content Item content with potential tags
   * @param updates Array to collect changes
   */
  private async processTagsUpdate(
    content: any,
    updates: Array<{ item: Item; revision: Revision }>,
  ): Promise<void> {
    if (content.tags && Array.isArray(content.tags)) {
      try {
        // Ensure global tags list exists
        await this.ensureGlobalTagsExist(false) // Don't send to server yet, we'll send as part of the updates

        // Get the global tag list
        const globalTagsId = this.dataStorage.getGlobalTagsItemId()
        const globalTagsData =
          await this.dataStorage.getDecryptedItemData(globalTagsId)

        let globalTags: Tag[] = []
        if (
          globalTagsData &&
          globalTagsData.tags &&
          Array.isArray(globalTagsData.tags)
        ) {
          globalTags = globalTagsData.tags
        }

        // Extract tag strings from content (assuming content.tags is an array of tag strings)
        const contentTagStrings = content.tags

        // Find any new tags that need to be added to the global list
        const newTags = contentTagStrings.filter(
          (tagName: string) => !globalTags.some((tag) => tag.name === tagName),
        )

        // If we have new tags, update the global tag list
        if (newTags.length > 0) {
          this.logger.info("Adding new tags to global tag list", { newTags })

          // Create new Tag objects for each new tag
          const newTagObjects = newTags.map((tagName: string) => ({
            id: generateId(),
            name: tagName,
            color: getTagColor(tagName),
          }))

          const updatedGlobalTags = [...globalTags, ...newTagObjects]

          // Update the global tag list
          const { item: tagsItem, revision: tagsRevision } =
            await this.dataStorage.updateItemPlaintext(
              globalTagsId,
              SyncItemType.Tags,
              { tags: updatedGlobalTags } as GlobalTagsList,
            )

          // Add the tags update to the list of updates
          updates.push({ item: tagsItem, revision: tagsRevision })
        }
      } catch (error) {
        this.logger.error("Failed to update global tag list", { error })
        // Continue with saving the item even if tag list update fails
      }
    }
  }

  /**
   * Updates the global tag list and propagates changes to all items that reference the modified tags
   * @param updatedTags The updated array of Tag objects
   * @param sendToServer Whether to send updates to server
   * @returns Array of updated items and revisions
   */
  private async updateGlobalTags(
    updatedTags: Tag[],
    sendToServer: boolean = true,
  ): Promise<Array<{ item: Item; revision: Revision }>> {
    if (!this.isAuthenticated) {
      throw new Error("User is not authenticated")
    }

    if (!this.encryptionKey) {
      this.logger.error(
        "Cannot update global tags - encryption key not available",
      )
      throw new Error("Encryption key is not available")
    }

    this.logger.info("Updating global tags list", {
      tagCount: updatedTags.length,
      sendToServer,
    })

    // Ensure global tags list exists
    await this.ensureGlobalTagsExist(sendToServer)

    // Get the current global tag list
    const globalTagsId = this.dataStorage.getGlobalTagsItemId()
    const globalTagsData =
      await this.dataStorage.getDecryptedItemData(globalTagsId)

    // Track all updates that need to be processed
    const updates: Array<{ item: Item; revision: Revision }> = []

    // Get the current global tags array (we know it exists now)
    const currentTags: Tag[] = globalTagsData?.tags || []

    // Find tags that have been renamed or deleted by comparing current with updated
    // A tag is considered renamed if the id matches but the name differs
    const changedTags: Array<{ oldTag: Tag; newTag: Tag | null }> = []

    // Find tags that were renamed or deleted
    for (const oldTag of currentTags) {
      const newTag = updatedTags.find((tag) => tag.id === oldTag.id)

      // If tag doesn't exist in updated tags or has a different name, it was changed or deleted
      if (!newTag || newTag.name !== oldTag.name) {
        changedTags.push({
          oldTag,
          newTag: newTag || null, // Will be null if the tag was deleted
        })
      }
    }

    // Update the global tags list
    const { item, revision } = await this.dataStorage.updateItemPlaintext(
      globalTagsId,
      SyncItemType.Tags,
      { tags: updatedTags } as GlobalTagsList,
    )
    updates.push({ item, revision })

    // If we have tags that were changed or deleted, update all items referencing them
    if (changedTags.length > 0) {
      this.logger.info("Tags were changed, updating references", {
        changedTagsCount: changedTags.length,
      })

      // Update tags in notes
      for (const change of changedTags) {
        await this.updateTagReferencesInAllItems(
          change.oldTag,
          change.newTag,
          updates,
        )
      }
    }

    // Send all updates to server if required
    if (sendToServer && updates.length > 0) {
      await this.sendUpdatesToServer(updates, SyncMessageType.UPDATE_ITEMS)
    }

    return updates
  }

  /**
   * Updates all items that reference a specific tag
   * @param oldTag The original tag
   * @param newTag The updated tag (or null if deleted)
   * @param updates Array to collect item updates
   */
  private async updateTagReferencesInAllItems(
    oldTag: Tag,
    newTag: Tag | null,
    updates: Array<{ item: Item; revision: Revision }>,
  ): Promise<void> {
    // Get all items with tags (notes and tasks)
    const notesItems = this.dataStorage.getItemsByType(SyncItemType.Note)
    const tasksItems = this.dataStorage.getItemsByType(SyncItemType.Task)

    // Combine all items that might have tags
    const allItems = { ...notesItems, ...tasksItems }

    for (const itemId of Object.keys(allItems)) {
      try {
        // Get decrypted item data
        const itemData = await this.dataStorage.getDecryptedItemData(itemId)

        // Skip if item doesn't have tags
        if (!itemData || !itemData.tags || !Array.isArray(itemData.tags)) {
          continue
        }

        // Check if this item references the tag we're looking for by name
        // Items reference tags by name, not by ID
        if (!itemData.tags.includes(oldTag.name)) {
          continue
        }

        this.logger.debug(`Updating tag reference in item ${itemId}`, {
          oldTagName: oldTag.name,
          newTagName: newTag ? newTag.name : null,
        })

        // Update the tags list in the item
        const updatedTags = itemData.tags
          .map((tagName: string) => {
            if (tagName === oldTag.name) {
              return newTag ? newTag.name : null // Replace with new name or remove if null
            }
            return tagName
          })
          .filter(Boolean) // Remove null values (deleted tags)

        // Create updated item data
        const updatedItemData = {
          ...itemData,
          tags: updatedTags,
        }

        // Save the updated item
        const { item, revision } = await this.dataStorage.updateItemPlaintext(
          itemId,
          allItems[itemId].type,
          updatedItemData,
        )

        updates.push({ item, revision })
      } catch (error) {
        this.logger.error(`Failed to update tag reference in item ${itemId}`, {
          error,
        })
        // Continue with other items even if one fails
      }
    }
  }

  /**
   * Marks an item as deleted (moved to bin) without permanently removing it
   * @param itemId Item ID to delete
   * @param sendToServer Whether to send update to server
   * @returns Updated item and revision
   */
  async deleteItem(
    itemId: string,
    sendToServer: boolean = true,
  ): Promise<{ item: Item; revision: Revision }> {
    if (!this.isAuthenticated) {
      throw new Error("User is not authenticated")
    }

    // Ensure the encryption key is set
    if (!this.encryptionKey) {
      this.logger.error("Cannot delete item - encryption key not available")
      throw new Error("Encryption key is not available")
    }

    this.logger.info("Moving item to bin", { itemId, sendToServer })

    // Get the current item data
    const item = this.dataStorage.getItem(itemId)
    if (!item) {
      throw new Error(`Item with ID ${itemId} not found`)
    }

    // Only handle actual content items, not system items like global tags
    const globalTagsId = this.dataStorage.getGlobalTagsItemId()
    if (itemId === globalTagsId) {
      throw new Error("Cannot delete the global tags item")
    }

    // Get the decrypted item data
    const itemData = await this.dataStorage.getDecryptedItemData(itemId)
    if (!itemData) {
      throw new Error(`Failed to get decrypted data for item ${itemId}`)
    }

    // Update the item data with inBin flag set to true
    const updatedItemData = {
      ...itemData,
      inBin: true,
    }

    // Update the item with the inBin flag
    const result = await this.dataStorage.updateItemPlaintext(
      itemId,
      item.type,
      updatedItemData,
    )

    // Send to server if required
    if (sendToServer) {
      await this.sendUpdatesToServer([result], SyncMessageType.UPDATE_ITEMS)
    }

    return result
  }

  /**
   * Restores an item from the bin
   * @param itemId Item ID to restore
   * @param sendToServer Whether to send update to server
   * @returns Updated item and revision
   */
  async restoreItem(
    itemId: string,
    sendToServer: boolean = true,
  ): Promise<{ item: Item; revision: Revision }> {
    if (!this.isAuthenticated) {
      throw new Error("User is not authenticated")
    }

    // Ensure the encryption key is set
    if (!this.encryptionKey) {
      this.logger.error("Cannot restore item - encryption key not available")
      throw new Error("Encryption key is not available")
    }

    this.logger.info("Restoring item from bin", { itemId, sendToServer })

    // Get the current item data
    const item = this.dataStorage.getItem(itemId)
    if (!item) {
      throw new Error(`Item with ID ${itemId} not found`)
    }

    // Get the decrypted item data
    const itemData = await this.dataStorage.getDecryptedItemData(itemId)
    if (!itemData) {
      throw new Error(`Failed to get decrypted data for item ${itemId}`)
    }

    // If the item is not in the bin, there's nothing to restore
    if (!itemData.inBin) {
      this.logger.warn("Item is not in the bin, nothing to restore", { itemId })
      return { item, revision: item.revisions[item.revisions.length - 1] }
    }

    // Update the item data with inBin flag set to false
    const updatedItemData = {
      ...itemData,
      inBin: false,
    }

    // Update the item with the inBin flag
    const result = await this.dataStorage.updateItemPlaintext(
      itemId,
      item.type,
      updatedItemData,
    )

    // Send to server if required
    if (sendToServer) {
      await this.sendUpdatesToServer([result], SyncMessageType.UPDATE_ITEMS)
    }

    return result
  }

  /**
   * Send updates to the server
   * @param updates Array of updates to send
   * @param messageType The message type to send (default: "UPDATE_ITEMS")
   */
  private async sendUpdatesToServer(
    updates: Array<{ item: Item; revision: Revision }>,
    messageType: SyncMessageType,
  ): Promise<void> {
    if (this.connectionStatus === "registered") {
      this.logger.info("Sending item update(s) to server", {
        itemCount: updates.length,
        itemIds: updates.map((u) => u.item.id),
      })

      // Convert items and revisions to a format suitable for syncing
      const syncUpdates = updates.map(({ item, revision }) => ({
        item: {
          id: item.id,
          type: item.type as any,
          createdAt: new Date(item.createdAt),
          updatedAt: new Date(item.updatedAt),
        },
        revision: {
          id: revision.id,
          itemId: revision.itemId,
          encryptedData: revision.encryptedData,
          createdAt: new Date(revision.createdAt),
        },
      }))

      // Send updates to server
      try {
        await this.pendingActions.addAction(() => {
          this.sendUpdate(messageType, {
            items: syncUpdates,
          })
          return Promise.resolve()
        })
      } catch (error) {
        this.logger.error("Failed to execute pending server updates action", {
          itemIds: syncUpdates.map((u) => u.item.id),
          error,
        })
      }
    }
  }

  /**
   * Ensures that global tags list exists; creates it if it doesn't
   * @param sendToServer Whether to send the newly created global tags to server
   * @returns true if tags were created, false if they already existed
   */
  public async ensureGlobalTagsExist(
    sendToServer: boolean = true,
  ): Promise<boolean> {
    if (!this.isAuthenticated) {
      throw new Error("User is not authenticated")
    }

    if (!this.encryptionKey) {
      this.logger.error(
        "Cannot ensure global tags - encryption key not available",
      )
      throw new Error("Encryption key is not available")
    }

    // Check if global tag list exists
    const globalTagsId = this.dataStorage.getGlobalTagsItemId()
    const globalTagsData =
      await this.dataStorage.getDecryptedItemData(globalTagsId)

    // If global tags don't exist yet, create them
    if (!globalTagsData) {
      this.logger.info("Creating global tags list")

      const { item, revision } = await this.dataStorage.updateItemPlaintext(
        globalTagsId,
        SyncItemType.Tags,
        { tags: [] } as GlobalTagsList,
      )

      // Send to server if required
      if (sendToServer) {
        await this.sendUpdatesToServer(
          [{ item, revision }],
          SyncMessageType.UPDATE_ITEMS,
        )
      }

      return true
    }

    return false
  }
}
